import { FieldMapShape } from './field-map-shape';
import * as L from 'leaflet';
import { Study } from './study';
import * as turf from '@turf/turf';
import { ObservationUnit } from '../model/observationUnit';
import { FieldMapContext } from '../field-map-context.model';
import { convertPolygonToTurf, isPointInPolygon } from './shape-utils';

export class Plot extends FieldMapShape {
    id = '';
    isEditing = false;
    isSelected = false;
    name = '';
    polygon: L.Polygon | null = null;
    parentStudy: Study | null = null;
    editInitialLatLng?: L.LatLng[][];
    type: 'PLOT' = 'PLOT';
    turfBoundingBoxPolygon?: turf.helpers.Feature<turf.helpers.Polygon, {}>;
    isOutsideBoundary?: boolean;
    observationUnit?: ObservationUnit;
    crop: string;

    constructor(params: {
        map: L.Map,
        id: string,
        name: string,
        polygon: L.Polygon,
        parent: Study,
        observationUnit: ObservationUnit,
        fieldMapContext: FieldMapContext,
        crop: string
    }) {
        super(params.map);
        this.id = params.id;
        this.name = params.name;
        this.polygon = params.polygon;
        this.parentStudy = params.parent;
        this.observationUnit = params.observationUnit;
        this.fieldMapContext = params.fieldMapContext;
        this.crop = params.crop;
        this.initialize();
    }

    initialize() {
        this.parentStudy.plots.set(this.id, this);
        super.initialize();
    }

    draw(): void {
        this.parentStudy.plotsDrawingLayer.addLayer(this.polygon);
        this.updateStyle('DEFAULT');
        this.initialize();
        super.draw();
    }

    delete(): void {
    }

    removeListeners() {
        super.removeListeners();
        this.polygon.off('editable:vertex:dragstart');
        this.polygon.off('editable:vertex:drag');
        this.polygon.off('editable:vertex:dragend');
    }

    addListeners(): void {
        super.addListeners();

        this.polygon.on('editable:vertex:dragstart', (e: any) => {
            const study = this.parentStudy;
            const originalLatLng = e.vertex.latlng.clone();
            if (study.parentBlock) {
                const turfBoundingPolygon = convertPolygonToTurf(study.parentBlock.polygon);

                const onDragEnd = () => {
                    const newLatLng = e.vertex.latlng;

                    if (!isPointInPolygon(newLatLng, turfBoundingPolygon)) {
                        // Point is outside bounding area. Reverting...
                        e.vertex.latlng.lat = originalLatLng.lat;
                        e.vertex.latlng.lng = originalLatLng.lng;
                        (this.polygon as any).editor.reset();
                        this.polygon.redraw();
                        study.parentBlock.updateStyle('DEFAULT');
                    }
                };

                const onDrag = () => {
                    const newLatLng = e.vertex.latlng;
                    if (!isPointInPolygon(newLatLng, turfBoundingPolygon)) {
                        // Point outside bounding area
                        study.parentBlock.updateStyle('BOUNDARY_ERROR');
                    } else {
                        study.parentBlock.updateStyle('DEFAULT');
                    }
                };

                e.vertex.on('drag', onDrag);
                e.vertex.once('dragend', onDragEnd);
            }
        });
    }

    async commit(): Promise<void> {
        return super.commit().then(() => {
            this.parentStudy.draw();
        });
    }

    reset() {
        super.reset();
    }

    edit(): void {
        super.edit();
        this.polygon.dragging.disable();
    }

    resize() {
        // No implementation
    }

    rotate() {
        // No implementation
    }

    isEditable(): boolean {
        return !this.isOutsideBoundary;
    }

    isResizable(): boolean {
        return false;
    }

    isRotatable(): boolean {
        return false;
    }

    isSaveable(): boolean {
        return this.isEditing && !this.isOutsideBoundary;
    }

    isDeletable(): boolean {
        return false;
    }

    select() {
        super.select();
    }

    unSelect() {
        super.unSelect();
    }
}
